import React, { useState } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { LayoutDashboard, Building2, FileText, LogOut, Menu, X, Database, ChevronDown, ChevronRight, Newspaper, Inbox } from 'lucide-react';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { icon: Inbox, label: 'Listing Requests', path: '/admin/listing-requests' },
  {
    icon: Database,
    label: 'Master Data',
    submenu: [
      { label: 'Country', path: '/admin/master/country' },
      { label: 'State', path: '/admin/master/state' },
      { label: 'City', path: '/admin/master/city' },
      { label: 'Industry', path: '/admin/master/industry' },
      { label: 'Employee Count', path: '/admin/master/employee-count' },
      { label: 'Topics', path: '/admin/master/topics' },
      { label: 'Tags', path: '/admin/master/tags' },
    ]
  },
  { icon: Building2, label: 'Startups', path: '/admin/startups' },
  {
    icon: FileText,
    label: 'Stories',
    submenu: [
      { label: 'Pending Submissions', path: '/admin/stories/pending-submissions' },
      { label: 'All Stories', path: '/admin/stories' },
      { label: 'Expert Requests', path: '/admin/expert-requests' },
    ]
  },
  { icon: Newspaper, label: 'Articles', path: '/admin/articles' },
];

export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [storiesOpen, setStoriesOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isPathActive = (path) => {
    return location.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    return submenu.some(item => location.pathname === item.path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-40 bg-card border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-accent rounded-lg"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/" className="font-heading font-bold text-xl">
              <span className="text-gradient-primary">Starticals</span>
              <span className="ml-2 text-xs bg-secondary/10 text-secondary px-2 py-1 rounded">Admin</span>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-sm">
              <span className="text-muted-foreground">Welcome, </span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`
            fixed lg:static inset-y-0 left-0 z-30 w-64 bg-card border-r border-border
            transform transition-transform duration-200 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            mt-[57px] lg:mt-0
          `}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              
              if (item.submenu) {
                const isActive = isSubmenuActive(item.submenu);
                const isOpen = item.label === 'Master Data' ? masterDataOpen : storiesOpen;
                const toggleOpen = item.label === 'Master Data' 
                  ? () => setMasterDataOpen(!masterDataOpen)
                  : () => setStoriesOpen(!storiesOpen);
                
                return (
                  <div key={item.label}>
                    <button
                      onClick={toggleOpen}
                      className={`
                        w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors
                        ${isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        {item.label}
                      </div>
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {isOpen && (
                      <div className="mt-1 ml-3 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                              block px-4 py-2 rounded-lg text-sm font-medium transition-colors text-left
                              ${isPathActive(subItem.path)
                                ? 'bg-primary text-primary-foreground'
                                : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                              }
                            `}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              
              const isActive = isPathActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors
                    ${isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground/80 hover:bg-accent hover:text-foreground'
                    }
                  `}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Sidebar Overlay for Mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
